import { api } from '../core/api';

// ============================================================================
// ТИПЫ
// ============================================================================

export interface RbacUser {
  id: string;
  azureOid: string;
  email: string;
  displayName: string;
  departmentId?: string;
  department?: string;
  jobTitle?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  dimUserId?: string;
  roles?: RbacRole[];
}

export interface RbacRole {
  id: string;
  name: string;
  value: string;
  description?: string;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  usersCount?: number;
  permissions?: RbacRolePermission[];
}

export interface RbacPermission {
  id: string;
  name: string;
  value: string;
  description?: string;
  priority?: number;
}

export interface RbacResource {
  id: string;
  name: string;
  value: string;
  description?: string;
  createdAt?: string;
}

export interface RbacRolePermission {
  id?: string; // ID записи из rbac_role_permissions (для удаления)
  resource: RbacResource;
  permission: RbacPermission;
}

export interface CurrentUserData {
  user: {
    id: string;
    azureOid: string;
    email: string;
    displayName: string;
    departmentId?: string;
    department?: string;
    jobTitle?: string;
    isActive: boolean;
    dimUserId?: string;
  };
  roles: RbacRole[];
  permissions: RbacRolePermission[];
  departmentAccess: Array<{ departmentId: string }>;
}

// Алиас для обратной совместимости
export type CurrentUser = CurrentUserData;

export interface SyncResult {
  success: boolean;
  timestamp: string;
  groupId: string;
  stats: {
    totalMembers: number;
    created: number;
    updated: number;
    deactivated: number;
  };
  errors: string[];
}

export interface GroupInfo {
  id: string;
  displayName: string;
  description?: string;
  mail?: string;
  mailEnabled?: boolean;
  securityEnabled?: boolean;
}

export interface DashboardStats {
  users: {
    total: number;
    active: number;
    inactive: number;
    withoutRoles: number;
  };
  roles: {
    total: number;
  };
  sync: {
    lastSync: string | null;
    status: 'success' | 'error' | 'unknown';
  };
}

export interface AuditLog {
  id: string;
  action: string;
  entity: string;
  entityId?: string;
  details?: string;
  user: {
    displayName: string;
    email: string;
  } | null;
  createdAt: string;
  ipAddress?: string;
}

// ============================================================================
// API СЕРВИС
// ============================================================================

export const rbacService = {
  // ============================================================================
  // ДАШБОРД И АУДИТ
  // ============================================================================

  /**
   * Получение статистики для дашборда
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get<DashboardStats>('/rbac/stats');
    return response;
  },

  /**
   * Получение журнала аудита
   */
  async getAuditLogs(params?: { page?: number; limit?: number }): Promise<{
    logs: AuditLog[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await api.get<{
      logs: AuditLog[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/rbac/audit?${queryParams.toString()}`);
    return response;
  },

  // ============================================================================
  // ТЕКУЩИЙ ПОЛЬЗОВАТЕЛЬ

  // ============================================================================

  /**
   * Получение информации о текущем пользователе с правами
   * 
   * Использует увеличенный timeout (60 секунд) и автоматический retry,
   * так как это критически важный запрос для работы приложения.
   */
  async getCurrentUser(): Promise<CurrentUserData> {
    const response = await api.get<CurrentUserData>('/rbac/me', {
      timeout: 60000, // 60 секунд для критичного запроса
      maxRetries: 3,  // Автоматический retry при ошибках
    });
    return response;
  },

  // ============================================================================
  // ПОЛЬЗОВАТЕЛИ
  // ============================================================================

  /**
   * Получение списка пользователей
   */
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    active?: boolean;
  }): Promise<{
    users: RbacUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());

    const response = await api.get<{
      users: RbacUser[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/rbac/users?${queryParams.toString()}`);
    if (!response) throw new Error('Invalid response format from /rbac/users');
    return response;
  },

  /**
   * Получение данных пользователя по ID
   */
  async getUser(userId: string): Promise<CurrentUserData> {
    const response = await api.get<CurrentUserData>(`/rbac/users/${userId}`);
    return response;
  },

  /**
   * Обновление пользователя
   */
  async updateUser(userId: string, data: { isActive?: boolean; departmentId?: string }): Promise<RbacUser> {
    const response = await api.put<RbacUser>(`/rbac/users/${userId}`, data);
    return response;
  },

  /**
   * Принудительная пересинхронизация пользователя со справочниками 1С.
   * Используется, когда у юзера пустой dimUserId (email появился в dim_* уже после первой авторизации).
   */
  async syncUserWith1C(userId: string): Promise<{
    linked: boolean;
    changed: boolean;
    dimUserId: string | null;
    departmentId: string | null;
    department: string | null;
  }> {
    const response = await api.post<{
      linked: boolean;
      changed: boolean;
      dimUserId: string | null;
      departmentId: string | null;
      department: string | null;
    }>(`/rbac/users/${userId}/sync-1c`, {});
    return response;
  },

  /**
   * Назначение роли пользователю
   */
  async assignRole(userId: string, roleId: string): Promise<void> {
    await api.post(`/rbac/users/${userId}/roles`, { roleId });
  },

  /**
   * Удаление роли у пользователя
   */
  async removeRole(userId: string, roleId: string): Promise<void> {
    await api.delete(`/rbac/users/${userId}/roles/${roleId}`);
  },

  /**
   * Batch обновление ролей пользователя (добавление и удаление за один запрос)
   */
  async batchUpdateUserRoles(
    userId: string,
    data: { add?: string[]; remove?: string[] }
  ): Promise<{
    added: string[];
    removed: string[];
    errors: string[];
  }> {
    const response = await api.put<{
      added: string[];
      removed: string[];
      errors: string[];
    }>(`/rbac/users/${userId}/roles`, data);
    return response;
  },

  /**
   * Массовое назначение/удаление роли у нескольких пользователей
   */
  async bulkUpdateUsersRoles(
    userIds: string[],
    roleId: string,
    action: 'add' | 'remove'
  ): Promise<{
    processed: number;
    failed: number;
    successUserIds: string[];
    failedUserIds: string[];
  }> {
    const response = await api.post<{
      processed: number;
      failed: number;
      successUserIds: string[];
      failedUserIds: string[];
    }>('/rbac/users/bulk/roles', { userIds, roleId, action });
    return response;
  },

  /**
   * Экспорт пользователей в CSV с учётом фильтров.
   * Используем fetch напрямую, т.к. ApiClient парсит JSON, а нам нужен blob.
   */
  async exportUsers(params?: {
    search?: string;
    role?: string;
    active?: boolean;
  }): Promise<void> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.active !== undefined) queryParams.append('active', params.active.toString());

    const baseUrl = api.getBaseUrl();
    const url = `${baseUrl}/rbac/users/export?${queryParams.toString()}`;

    const headers: Record<string, string> = {};
    const { useAppStore } = await import('../../stores/useAppStore');
    const getAccessToken = useAppStore.getState().getAccessToken;
    if (getAccessToken) {
      const token = await getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('Ошибка экспорта пользователей');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  },

  // ============================================================================
  // РОЛИ
  // ============================================================================

  /**
   * Получение списка ролей
   */
  async getRoles(): Promise<RbacRole[]> {
    const response = await api.get<RbacRole[]>('/rbac/roles');
    if (!response) throw new Error('Invalid response format from /rbac/roles');
    return response;
  },

  /**
   * Создание роли
   */
  async createRole(data: {
    name: string;
    value: string;
    description?: string;
    isDefault?: boolean;
  }): Promise<RbacRole> {
    const response = await api.post<RbacRole>('/rbac/roles', data);
    return response;
  },

  /**
   * Обновление роли
   */
  async updateRole(
    roleId: string,
    data: { name?: string; description?: string; isDefault?: boolean }
  ): Promise<RbacRole> {
    const response = await api.put<RbacRole>(`/rbac/roles/${roleId}`, data);
    return response;
  },

  /**
   * Удаление роли
   */
  async deleteRole(roleId: string): Promise<void> {
    await api.delete(`/rbac/roles/${roleId}`);
  },

  /**
   * Назначение прав роли
   */
  async assignPermissionToRole(roleId: string, resourceId: string, permissionId: string): Promise<void> {
    await api.post(`/rbac/roles/${roleId}/permissions`, { resourceId, permissionId });
  },

  /**
   * Удаление прав у роли
   */
  async removePermissionFromRole(roleId: string, permissionRecordId: string): Promise<void> {
    await api.delete(`/rbac/roles/${roleId}/permissions/${permissionRecordId}`);
  },

  /**
   * Batch обновление прав роли (добавление и удаление за один запрос)
   */
  async batchUpdateRolePermissions(
    roleId: string,
    data: {
      add?: Array<{ resourceId: string; permissionId: string }>;
      remove?: string[]; // массив permissionRecordId
    }
  ): Promise<{
    added: Array<{ resourceId: string; permissionId: string }>;
    removed: string[];
    errors: string[];
  }> {
    const response = await api.put<{
      added: Array<{ resourceId: string; permissionId: string }>;
      removed: string[];
      errors: string[];
    }>(`/rbac/roles/${roleId}/permissions`, data);
    return response;
  },

  // ============================================================================
  // ПРАВА
  // ============================================================================

  /**
   * Получение списка прав
   */
  async getPermissions(): Promise<RbacPermission[]> {
    const response = await api.get<RbacPermission[]>('/rbac/permissions');
    return response;
  },

  /**
   * Создание права
   */
  async createPermission(data: { name: string; value: string; description?: string; priority?: number }): Promise<RbacPermission> {
    const response = await api.post<RbacPermission>('/rbac/permissions', data);
    return response;
  },

  /**
   * Обновление права
   */
  async updatePermission(id: string, data: { name: string; description?: string; priority?: number }): Promise<RbacPermission> {
    const response = await api.put<RbacPermission>(`/rbac/permissions/${id}`, data);
    return response;
  },

  /**
   * Удаление права
   */
  async deletePermission(id: string): Promise<void> {
    await api.delete(`/rbac/permissions/${id}`);
  },

  // ============================================================================
  // РЕСУРСЫ
  // ============================================================================

  /**
   * Получение списка ресурсов
   */
  async getResources(): Promise<RbacResource[]> {
    const response = await api.get<RbacResource[]>('/rbac/resources');
    return response;
  },

  /**
   * Создание ресурса
   */
  async createResource(data: { name: string; value: string; description?: string }): Promise<RbacResource> {
    const response = await api.post<RbacResource>('/rbac/resources', data);
    return response;
  },

  /**
   * Обновление ресурса
   */
  async updateResource(id: string, data: { name: string; description?: string }): Promise<RbacResource> {
    const response = await api.put<RbacResource>(`/rbac/resources/${id}`, data);
    return response;
  },

  /**
   * Удаление ресурса
   */
  async deleteResource(id: string): Promise<void> {
    await api.delete(`/rbac/resources/${id}`);
  },

  // ============================================================================
  // СИНХРОНИЗАЦИЯ С ENTRA ID
  // ============================================================================

  /**
   * Запуск синхронизации с группой Entra ID
   */
  async syncEntraGroup(groupId?: string): Promise<SyncResult> {
    const response = await api.post<SyncResult>('/rbac/sync/entra', { groupId });
    return response;
  },

  /**
   * Получение статуса последней синхронизации
   */
  async getSyncStatus(): Promise<SyncResult | null> {
    const response = await api.get<SyncResult | null>('/rbac/sync/status');
    return response;
  },

  /**
   * Получение информации о группе Entra ID
   */
  async getGroupInfo(groupId?: string): Promise<GroupInfo> {
    const queryParams = groupId ? `?groupId=${groupId}` : '';
    const response = await api.get<GroupInfo>(`/rbac/sync/group-info${queryParams}`);
    return response;
  },
};


